import React, { useEffect, useState } from 'react';
import { da, de, enCA } from 'date-fns/locale';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@material-ui/icons/Clear';
import { Grid, IconButton } from '@material-ui/core';

export function now(): Date {
  const date = new Date();
  // used as to date
  date.setHours(23);
  date.setMinutes(59);
  date.setSeconds(59);
  date.setMilliseconds(0);
  return date;
}

export function lastMonth(): Date {
  const date = now();
  // used as from date
  date.setMonth(date.getMonth() - 1);
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
}

export function utcToLocalWithoutTz(utc: Date) {
  return new Date(
    utc.getUTCFullYear(),
    utc.getUTCMonth(),
    utc.getUTCDate(),
    utc.getUTCHours(),
    utc.getUTCMinutes(),
    utc.getUTCSeconds(),
  );
}

export function localToUTCWithoutTz(local: Date) {
  const utc = new Date(
    Date.UTC(
      local.getFullYear(),
      local.getMonth(),
      local.getDate(),
      local.getHours(),
      local.getMinutes(),
      local.getSeconds(),
    ),
  );
  return utc;
}

type DateRangeProps = {
  clearable?: boolean;

  defaultStartDate?: Date | null;
  defaultEndDate?: Date | null;

  startDate?: Date | null;
  endDate?: Date | null;

  onChange?: (from: Date | null, to: Date | null, valid: boolean) => void;

  itemClass?: string;

  utc?: boolean; // whether input/output dates should be utc dates
};

const DateRangePickerComponent: React.FC<DateRangeProps> = (props) => {
  const [startDate, setStartDate] = useState<Date | null>(
    props.defaultStartDate ?? null,
  );
  const [endDate, setEndDate] = useState<Date | null>(
    props.defaultEndDate ?? null,
  );
  const { t } = useTranslation(['translations']);

  useEffect(() => {
    if (props.startDate !== undefined) {
      setStartDate(
        props.utc && props.startDate
          ? utcToLocalWithoutTz(props.startDate)
          : props.startDate,
      );
    }
  }, [props.startDate, props.utc]);

  useEffect(() => {
    if (props.endDate !== undefined) {
      setEndDate(
        props.utc && props.endDate
          ? utcToLocalWithoutTz(props.endDate)
          : props.endDate,
      );
    }
  }, [props.endDate, props.utc]);

  const getLanguage = (): Locale => {
    const language = navigator.language;
    switch (language) {
      case 'da-DK':
        return da;
      case 'en-US':
      case 'en-CA':
        return enCA;
      case 'de-AT':
      case 'de-DE':
      case 'de-CH':
        return de;
      default:
        return enCA;
    }
  };

  const getFormat = (): string => {
    const language = navigator.language;
    switch (language) {
      case 'da-DK':
      case 'de-AT':
      case 'de-DE':
      case 'de-CH':
        return 'dd/MM/yyyy';
      case 'en-US':
      case 'en-CA':
        return 'MM/dd/yyyy';
      default:
        return 'dd/MM/yyyy';
    }
  };

  const notifyChange = (from: Date | null, to: Date | null) => {
    if (props.onChange) {
      let valid = true;
      if (from) {
        if (isNaN(from.getTime())) {
          // invalid date
          valid = false;
        } else {
          from.setHours(0);
          from.setMinutes(0);
          from.setSeconds(0);
          from.setMilliseconds(0);
        }
      }
      if (to) {
        if (isNaN(to.getTime())) {
          // invalid date
          valid = false;
        } else {
          to.setHours(23);
          to.setMinutes(59);
          to.setSeconds(59);
          to.setMilliseconds(0);
        }
      }

      if (from && to && from > to) {
        valid = false;
      }

      props.onChange(
        props.utc && from ? localToUTCWithoutTz(from) : from,
        props.utc && to ? localToUTCWithoutTz(to) : to,
        valid,
      );
    }
  };

  const setStartDateFn = (date: Date | null) => {
    setStartDate(date);
    notifyChange(date, endDate);
  };

  const setEndDateFn = (date: Date | null) => {
    setEndDate(date);
    notifyChange(startDate, date);
  };

  const invalidOrderMessage = t('date_range.invalid_range');
  const invalidDateMessage = t('date_range.invalid_date');

  const invalidFrom = !!startDate && isNaN(startDate.getTime());
  const invalidTo = !!endDate && isNaN(endDate.getTime());
  const orderError = !!startDate && !!endDate && startDate > endDate;

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={getLanguage()}>
      <Grid item className={props.itemClass}>
        <KeyboardDatePicker
          fullWidth
          variant="inline"
          inputVariant="outlined"
          value={startDate}
          onChange={setStartDateFn}
          format={getFormat()}
          autoOk
          label={t('date_range.from')}
          InputAdornmentProps={{
            position: 'start',
          }}
          error={orderError || invalidFrom}
          helperText={
            invalidFrom
              ? invalidDateMessage
              : orderError
              ? invalidOrderMessage
              : undefined
          }
          InputProps={
            props.clearable
              ? {
                  endAdornment: (
                    <IconButton
                      onClick={() => {
                        setStartDateFn(null);
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  ),
                }
              : undefined
          }
        />
      </Grid>
      <Grid item className={props.itemClass}>
        <KeyboardDatePicker
          fullWidth
          variant="inline"
          inputVariant="outlined"
          value={endDate}
          onChange={setEndDateFn}
          format={getFormat()}
          autoOk
          error={orderError || invalidTo}
          label={t('date_range.to')}
          InputAdornmentProps={{ position: 'start' }}
          InputProps={
            props.clearable
              ? {
                  endAdornment: (
                    <IconButton
                      onClick={() => {
                        setEndDateFn(null);
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  ),
                }
              : undefined
          }
          invalidDateMessage={invalidDateMessage}
        />
      </Grid>
    </MuiPickersUtilsProvider>
  );
};

export { DateRangePickerComponent };
